import { dayjs } from 'utils/dayjs';
import { parseTime } from 'utils/time';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { serviceSchedule } from '../services/service';
import type { Shift, ShiftSegmentType } from '../types/services';
import type { ShiftSegmentForm, UpdateShiftFormState, UseFormUpdateShiftProps } from '../types/hooks';
import { usePermisoSchedule } from '../../../../auth/permisosModulos';

const emptyState = (): UpdateShiftFormState => ({
    employeeId: '',
    date: dayjs(),
    segments: [],
});

const createDefaultSegment = (): ShiftSegmentForm => ({
    id: Date.now(),
    start: dayjs().startOf('day'),
    end: dayjs().startOf('day'),
    type: 'REGULAR',
});

const hydrateShift = (shift: Shift): UpdateShiftFormState => {
    const date = dayjs(shift.date, 'YYYY-MM-DD');
    const segments: ShiftSegmentForm[] = shift.segments.length
        ? shift.segments.map((segment) => ({
              id: Number(segment.segment_id) || Date.now() + Math.random(),
              start: dayjs(segment.start_ts),
              end: dayjs(segment.end_ts),
              type: segment.type === 'BREAK' ? 'BREAK' : 'REGULAR',
          }))
        : [
              {
                  id: Number(shift.shift_id) || Date.now(),
                  start: parseTime(shift.date, shift.start_ts),
                  end: parseTime(shift.date, shift.end_ts),
                  type: 'REGULAR' as ShiftSegmentType,
              },
          ];

    return {
        employeeId: String(shift.employee_id),
        date: date.isValid() ? date : dayjs(),
        segments,
    };
};

const isSameState = (a: UpdateShiftFormState, b: UpdateShiftFormState) =>
    a.employeeId === b.employeeId &&
    (a.date?.valueOf() ?? null) === (b.date?.valueOf() ?? null) &&
    a.segments.length === b.segments.length &&
    a.segments.every((segment, index) => {
        const other = b.segments[index];
        return segment.type === other.type && (segment.start?.valueOf() ?? null) === (other.start?.valueOf() ?? null) && (segment.end?.valueOf() ?? null) === (other.end?.valueOf() ?? null);
    });

const useFormUpdateShift = ({ employees, shift, shiftId, reload, option, setOption }: UseFormUpdateShiftProps) => {
    const { t } = useTranslation('schedule.form.edit_shift');
    const { showAlert } = useRbSystemAlert();
    const canEditShift = usePermisoSchedule('update_shift');
    const [openForm, setOpenForm] = useState(false);
    const [formState, setFormState] = useState<UpdateShiftFormState>(emptyState());
    const [originalState, setOriginalState] = useState<UpdateShiftFormState>(emptyState());
    const [searchEmployee, setSearchEmployee] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);

    useEffect(() => {
        if (option === 'edit_shift' && shiftId) {
            if (canEditShift) setOpenForm(true);
            setOption(null);
        }
    }, [option, shiftId, setOption, canEditShift]);

    useEffect(() => {
        if (!openForm || !shift) return;

        const next = hydrateShift(shift);
        setFormState(next);
        setOriginalState(next);
        setValidationMessage('');
        setShowMessage(false);
    }, [openForm, shift]);

    const closeForm = () => {
        setOpenForm(false);
        setFormState(emptyState());
        setOriginalState(emptyState());
        setSearchEmployee('');
        setValidationMessage('');
        setShowMessage(false);
    };

    const hasChanges = () => !isSameState(formState, originalState);

    const addSegment = () => {
        const previousSegment = formState.segments.at(-1);
        const nextStart = previousSegment?.end ?? dayjs().startOf('day');

        setFormState((previousState) => ({
            ...previousState,
            segments: [
                ...previousState.segments,
                {
                    ...createDefaultSegment(),
                    start: nextStart,
                    end: nextStart,
                },
            ],
        }));
    };

    const updateSegment = (segmentId: number, changes: Partial<Omit<ShiftSegmentForm, 'id'>>) => {
        setFormState((previousState) => ({
            ...previousState,
            segments: previousState.segments.map((segment) => (segment.id === segmentId ? { ...segment, ...changes } : segment)),
        }));
    };

    const removeSegment = (segmentId: number) => {
        setFormState((previousState) => ({
            ...previousState,
            segments: previousState.segments.filter((segment) => segment.id !== segmentId),
        }));
    };

    const submit = async () => {
        if (!shiftId || !canEditShift) return;

        if (!formState.employeeId || !formState.date?.isValid()) {
            setValidationMessage(t('edit_shift.alerts.validation.required'));
            return;
        }

        if (
            formState.segments.length === 0 ||
            formState.segments.some((segment) => !segment.start?.isValid() || !segment.end?.isValid() || !segment.end.isAfter(segment.start) || !segment.end.isSame(segment.start, 'day'))
        ) {
            setValidationMessage(t('edit_shift.alerts.validation.segments'));
            return;
        }

        setLoading(true);

        try {
            const ok = await serviceSchedule.updateShift({
                shiftId,
                body: {
                    date: formState.date.format('YYYY-MM-DD'),
                    segments: formState.segments.map((segment) => ({
                        type: segment.type,
                        start_time: segment.start!.format('HH:mm:ss'),
                        end_time: segment.end!.format('HH:mm:ss'),
                    })),
                },
                alert: { t, showAlert },
            });

            if (ok) {
                await reload();
                closeForm();
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        t,
        title: t('edit_shift.title'),
        openForm,
        setOpenForm,
        closeForm,
        employees: employees.map((employee) => ({ ...employee, value: String(employee.value) })),
        formState,
        setFormState,
        searchEmployee,
        setSearchEmployee,
        loading,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        hasChanges,
        addSegment,
        updateSegment,
        removeSegment,
        submit,
    };
};

export { useFormUpdateShift };
